import React, { useEffect, useReducer, useRef, useState } from 'react';
import NumberInput from '../../../../components/bearing-search/components/forms/NumberInput';
import { convertNumberInput } from '../../../utils/validations';
import { speedConversionReducer, speedConversionInitialState, SpeedConversionState } from './reducer';
import { useConversion } from '../../ConversionContext';
import {
	getConvertedUnit,
	getConvertedAngularSpeed,
	getConvertedLinearSpeeds,
} from '../../../../components/bearing-search/api/api';
import {
	ConversionUnitsPayload,
	ConversionAngularSpeedPayload,
	ConversionLinearSpeedPayload,
	ConversionUnitsResponse,
} from '../../../../components/bearing-search/types/types';
import { ConversionToolTabProps } from '../../types';
import { mapConversionAnalyicsData } from '../../Utils/AnalyticsUtils';
import { tabInputDebounceFunction, isSingleDigitNumber } from '../Utils/TabUtils';
import { trackScientificNotationChangeEvent } from "../../../utils/analytics";

const TabSpeed: React.FC<ConversionToolTabProps> = ({ description }) => {
	const { exponent, precision } = useConversion();

	const debounceTimeout = useRef<NodeJS.Timeout | null>(null); // debounce conversion calls
	const debounceLinearTimeout = useRef<NodeJS.Timeout | null>(null); // debounce linear speed recalculation
	const debounceRotationTimeout = useRef<NodeJS.Timeout | null>(null); // debounce angular speed recalculation
	const [ allowLinearCalculation, setAllowLinearCalculation ] = useState(true);

	const lastInputRef = useRef<{
		category: 'linear' | 'radius' | 'rotation';
		unit: string;
		value: string;
	} | null>(null);

	const lastResetCategoryRef = useRef<'linear' | 'radius' | 'rotation' | null>(null);
	// Reducer
	const [ speedConversionState, dispatch ] = useReducer(
		speedConversionReducer,
		speedConversionInitialState,
	);
	const currentStateRef = useRef();

	/**
	 * Recalculate all conversions units
	 */
	const recalculateAllConversions = async () => {
		let changedCategories = [];

		for (const [ categoryKey, units ] of Object.entries(speedConversionState)) {
			const category = categoryKey as keyof SpeedConversionState;

			for (const [ unit, value ] of Object.entries(units)) {
				if (value !== undefined && value !== '' && value !== '0') {
					if (!changedCategories.includes(category)){
						changedCategories.push(category);
					}
					if (!changedCategories.includes('precision exponent') && exponent) {
						changedCategories.push('precision exponent');
					}
					if (!changedCategories.includes('precision') && precision != 4) {
						changedCategories.push('precision');
					}
					if (allowLinearCalculation) {
						/**
						 * If Allow Linear Calculation is set to Yes, set to false if any values were updated.
						 * Don't allow for FetchAngularSpeed call to run after this function completes (due to useEffect on kmph field)
						 */
						setAllowLinearCalculation(false);
					}
					callAndDispatchConversion(category, unit, value, true);
				}
			}
		}

		return changedCategories;
	};

	/**
	 * Reset all conversions units
	 */
	const resetAllConversions = () => {
		for (const [ categoryKey, units ] of Object.entries(speedConversionState)) {
			const category = categoryKey as keyof SpeedConversionState;

			for (const [value] of Object.entries(units)) {
				if (value !== undefined && value !== '' && value !== '0') {
					dispatch({
						type: 'reset_category',
						payload: {
							category,
						},
					});
				}
			}
		}
	};

	/**
	 * Update the converted values with the new exponent
	 * convert based on the first input of each category
	 */
	useEffect(() => {
		currentStateRef.current = speedConversionState;
		recalculateAllConversions().then((result) => {
			trackScientificNotationChangeEvent('conversionTool', result);
		});
	}, [ exponent ]);

	/**
	 * Reset the values when precision is changed
	 */
	useEffect(() => {
		resetAllConversions();
	}, [ precision ]);

	// Map of conversion types for API payload
	const conversionTypeMap: Record<string, string[]> = {
		kmph: [ 'kphToMeterPerSecond', 'kphToMph', 'kilometersPerHourToInchesPerSecond', 'kphToFeetPerSecond' ],
		mph: [ 'mphToKph', 'mphToMeterPerSecond', 'milesPerHourToInchPerSec', 'mphToFeetPerSecond' ],
		mps: [ 'meterPerSecondToKph', 'meterPerSecondToMph', 'meterToInch', 'meterPerSecondToFeetPerSecond' ],
		inps: [ 'inchesPerSecondToKilometersPerHour', 'inchPerSecToMilesPerHour', 'inchToMeter', 'inchTofeet' ],
		ftps: [ 'feetPerSecondToKph', 'feetPerSecondToMph', 'feetPerSecondToMeterPerSecond', 'feetToInch' ],
		mm: [ 'mmToinch' ],
		in: [ 'inchTomm' ],
		rpm: [ 'revolutionPerMinuteToRadianPerSecond' ],
		radps: [ 'radianperSecondToRevolutionPerMinute' ],
		linear: [
			'millimetersPerMinuteToKilometersPerHour',
			'millimetersPerMinuteToMilesPerHour',
			'millimetersPerMinuteToMetersPerSecond',
			'millimetersPerMinuteToInchesPerSecond',
			'millimetersPerMinuteToFeetPerSecond',
		],
	};

	// Map of converted units from API response
	const convertedUnitMap: Record<string, string> = {
		feetPerSecondToKph: 'kmph',
		feetPerSecondToMeterPerSecond: 'mps',
		feetPerSecondToMph: 'mph',
		feetToInch: 'inps',
		inchPerSecToMilesPerHour: 'mph',
		inchToMeter: 'mps',
		inchTofeet: 'ftps',
		inchTomm: 'mm',
		inchesPerSecondToKilometersPerHour: 'kmph',
		kilometersPerHourToInchesPerSecond: 'inps',
		kphToFeetPerSecond: 'ftps',
		kphToMeterPerSecond: 'mps',
		kphToMph: 'mph',
		meterPerSecondToFeetPerSecond: 'ftps',
		meterPerSecondToKph: 'kmph',
		meterPerSecondToMph: 'mph',
		meterToInch: 'inps',
		milesPerHourToInchPerSec: 'inps',
		millimetersPerMinuteToFeetPerSecond: 'ftps',
		millimetersPerMinuteToInchesPerSecond: 'inps',
		millimetersPerMinuteToKilometersPerHour: 'kmph',
		millimetersPerMinuteToMetersPerSecond: 'mps',
		millimetersPerMinuteToMilesPerHour: 'mph',
		mmToinch: 'in',
		mphToFeetPerSecond: 'ftps',
		mphToKph: 'kmph',
		mphToMeterPerSecond: 'mps',
		radianperSecondToRevolutionPerMinute: 'rpm',
		revolutionPerMinuteToRadianPerSecond: 'radps',
	};

	/**
	 * handle API response
	 *
	 * @param  response
	 * @param  category
	 */
	const handleAPIConversionResponse = (response: ConversionUnitsResponse, category: 'linear' | 'radius' | 'rotation') => {
		if (response.unitConversionResults && category !== lastResetCategoryRef.current) {
			for (const conversionResult of response.unitConversionResults) {
				const convertedType = conversionResult.convertedTypes;
				const convertedValue = conversionResult.convertedValue;
				const convertedUnit = convertedUnitMap[ convertedType ];

				if (! convertedUnit) {
					console.log(`${ convertedType } index doesn't exit in convertedUnitMap`);
					return;
				}

				const converted = {
					[ convertedUnit ]: convertedValue.replaceAll(/,/g, ''),
				};

				dispatch({
					type: 'update_unit',
					payload: {
						category,
						updatedUnits: converted,
					},
				});
			}
		} else {
			dispatchResetCategory(category);
		}
	};

	/**
	 * Call direct conversion
	 *
	 * @param  conversionTypeArray
	 * @param  inputValue
	 * @param  category
	 * @param skipAnalyticCall
	 */
	const callAPIConvertUnit = async (conversionTypeArray: string[], inputValue: string, category: 'linear' | 'radius' | 'rotation', skipAnalyticCall: boolean) => {
		const conversionPayload: ConversionUnitsPayload = {
			unitConversionInputs: [
				{
					conversionType: conversionTypeArray,
					inputValue,
					precision,
					exponent,
				},
			],
		};

		const response = await getConvertedUnit(conversionPayload);
		// ANALYTICS START
		if (!skipAnalyticCall) {
			const categoryAnalyticsMap = {
				linear: 'linearSpeed',
				radius: 'radiusSpeed',
				rotation: 'rotation'
			};
			// ANALYTICS
			mapConversionAnalyicsData(category ? categoryAnalyticsMap[category] : category, conversionPayload, response);
		}

		handleAPIConversionResponse(response, category);
	};

	/**
	 * Call angular speed conversion
	 *
	 * @param  conversionTypeArray
	 * @param  inputValue
	 * @param  category
	 */
	const callAPIConvertAngularSpeed = async () => {
		if (allowLinearCalculation) {
			const conversionTypeArray = conversionTypeMap.rpm || [];
			if (conversionTypeArray.length === 0) {
				return;
			}

			const inputValue = ''; // pass an empty value, endpoint required

			const conversionPayload: ConversionAngularSpeedPayload = {
				angularSpeedInputs: [
					{
						conversionType: conversionTypeArray,
						inputValue,
						radius: Number.parseFloat(speedConversionState.radius.mm ?? '0'),
						linearSpeed: Number.parseFloat(speedConversionState.linear.kmph ?? '0'),
						linearSpeedType: 'KMH',
						precision,
						exponent,
					},
				],
			};

			const response = await getConvertedAngularSpeed(conversionPayload);
			handleAPIConversionResponse(response, 'rotation');

			// additionally, we have to extract the 'inputValue' from the response
			// this value is the RPM value
			const rpmValue = response.unitConversionResults[ 0 ].inputValue;
			const convertedObj = {
				rpm: rpmValue.replace(/,/g, ''),
			};

			// dispatch rpm update
			dispatch({
				type: 'update_unit',
				payload: {
					category: 'rotation',
					updatedUnits: convertedObj,
				},
			});

			// ANALYTICS
			mapConversionAnalyicsData('rotation', conversionPayload, response);
		}
		/**
		 * If not run, means the Scientific Notation checkbox was checked (or unchecked). Reset to allow in the event user
		 * wants to change these fields manually
		 */
		setAllowLinearCalculation(true);
	};

	/**
	 * Call angular speed conversion
	 *
	 * @param  conversionTypeArray
	 * @param  inputValue
	 * @param  category
	 */
	const callAPIConvertLinearSpeed = async () => {
		const conversionTypeArray = conversionTypeMap.linear || [];
		if (conversionTypeArray.length === 0) {
			return;
		}

		const inputValue = ''; // pass an empty value, endpoint required

		const conversionPayload: ConversionLinearSpeedPayload = {
			linearSpeedData: [
				{
					conversionType: conversionTypeArray,
					inputValue,
					radius: Number.parseFloat(speedConversionState.radius.mm ?? '0'),
					rotation: Number.parseFloat(speedConversionState.rotation.rpm ?? '0'),
					precision,
					exponent,
				},
			],
		};

		const response = await getConvertedLinearSpeeds(conversionPayload);
		handleAPIConversionResponse(response, 'linear');

		// ANALYTICS
		mapConversionAnalyicsData('linear', conversionPayload, response);
	};

	/**
	 * @param  category
	 * @param  unit
	 * @param  value
	 * @param skipAnalyticCall
	 */
	const callAndDispatchConversion = async (
		category: 'linear' | 'radius' | 'rotation',
		unit: string,
		value: string,
		skipAnalyticCall: boolean
	) => {
		const conversionTypeArray = conversionTypeMap[ unit ] || [];
		if (conversionTypeArray.length === 0) {
			return;
		}

		try {
			callAPIConvertUnit(conversionTypeArray, value, category, skipAnalyticCall);
		} catch (error) {
			console.error('Conversion API failed', error);
		}
	};

	/**
	 * Check if a linear and radius inputs have value to be used on rotation conversion
	 *
	 * @param  state
	 */
	const hasLinearAndRadiusInput = (): boolean => {
		const kmphHasValue = !! speedConversionState.linear.kmph && speedConversionState.linear.kmph !== '0';
		const mmHasValue = !! speedConversionState.radius.mm && speedConversionState.radius.mm !== '0';
		return kmphHasValue && mmHasValue;
	};

	/**
	 * Check if a rotation and radius inputs have value to be used on linear conversion
	 */
	const hasRotationAndRadiusInput = (): boolean => {
		const rpmHasValue = !! speedConversionState.rotation.rpm && speedConversionState.rotation.rpm !== '0';
		const mmHasValue = !! speedConversionState.radius.mm && speedConversionState.radius.mm !== '0';
		return rpmHasValue && mmHasValue;
	};

	/**
	 * Calculate linear speed using radius and rpm values
	 * when rpm value changes
	 */
	useEffect(() => {
		if (! lastInputRef.current) {
			return;
		}

		const { category, value } = lastInputRef.current;

		if (hasRotationAndRadiusInput() && category === 'rotation') {
			if (debounceRotationTimeout.current) {
				clearTimeout(debounceRotationTimeout.current);
			}

			const debounceDelay = value.length < 2 ? 0 : 600 + (value.length * 100);

			const isSingleInteger = isSingleDigitNumber(value);

			debounceRotationTimeout.current = setTimeout(() => {
				if (value.lastIndexOf('.') === value.length - 1 || isSingleInteger) {
					// don't call API immediately if the last character is a dot or if the value is a single integer
					if (isSingleInteger) {
						// check for single integer, and reset the field after 1.5 seconds
						setTimeout(() => {
							const currentValue = Number.parseFloat(lastInputRef.current?.value ?? '');
							// check for that the value is still single integer before submitting
							if (isSingleDigitNumber(currentValue.toString())) {
								callAPIConvertLinearSpeed();
							}
						}, 500);
					}
					return;
				}
				callAPIConvertLinearSpeed();
			}, debounceDelay);
		}
	}, [ speedConversionState.rotation.rpm ]);

	/**
	 * Calculate angular speed using radius and kmph values
	 * when kmph value changes
	 */
	useEffect(() => {
		if (! lastInputRef.current) {
			return;
		}

		const { category, value } = lastInputRef.current;

		if (hasLinearAndRadiusInput() && category === 'linear') {
			if (debounceLinearTimeout.current) {
				clearTimeout(debounceLinearTimeout.current);
			}

			const debounceDelay = value.length < 2 ? 0 : 600 + (value.length * 100);
			const isSingleInteger = isSingleDigitNumber(value);

			debounceRotationTimeout.current = setTimeout(() => {
				if (value.lastIndexOf('.') === value.length - 1 || isSingleInteger) {
					// don't call API immediately if the last character is a dot or if the value is a single integer
					if (isSingleInteger) {
						// check for single integer, and reset the field after 1.5 seconds
						setTimeout(() => {
							const currentValue = Number.parseFloat(lastInputRef.current?.value ?? '');
							// check for that the value is still single integer before submitting
							if (isSingleDigitNumber(currentValue.toString())) {
								callAPIConvertAngularSpeed();
							}
						}, 500);
					}
					return;
				}

				callAPIConvertAngularSpeed();
			}, debounceDelay);
		}
	}, [ speedConversionState.linear.kmph ]);

	//
	const dispatchResetCategory = (category: 'linear' | 'radius' | 'rotation') => {
		dispatch({
			type: 'reset_category',
			payload: { category },
		});

		lastResetCategoryRef.current = category;
	};

	/**
	 * Direct conversion inputs
	 *
	 * @param  category
	 * @param  unit
	 * @param  value
	 */
	const handleInputChange = (
		category: 'linear' | 'radius' | 'rotation',
		unit: string,
		value: string,
	) => {
		const numericValue = convertNumberInput(value);

		if (numericValue === undefined) {
			dispatch({
				type: 'reset_category',
				payload: { category },
			});

			lastResetCategoryRef.current = category;

			return; // return, don't call API
		}

		dispatch({
			type: 'update_unit',
			payload: {
				category,
				updatedUnits: { [ unit ]: value },
			},
		});

		lastResetCategoryRef.current = null;
		lastInputRef.current = { category, unit, value };

		// if category is radius, reset linear and rotation
		if (category === 'radius') {
			dispatchResetCategory('rotation');
			dispatchResetCategory('linear');
		}

		// restart debounceTimeout
		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
		}

		const debounceDelay = value.length < 2 ? 0 : 600 + (value.length * 100);

		debounceTimeout.current = setTimeout(() => {
			tabInputDebounceFunction(lastInputRef, callAndDispatchConversion);
		}, debounceDelay);
	};

	/**
	 * @param currentValue
	 * @param category
	 * @param unit
	 * @param newValue
	 */
	const handleBlur = (
		currentValue: string,
		category: 'linear' | 'radius' | 'rotation',
		unit: string,
		newValue: string,
	) => {
		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
			debounceTimeout.current = null;
		}

		const numericValue = Number.parseFloat(newValue);
		if (Number.isNaN(numericValue) || numericValue === 0) {
			return;
		}

		if (currentValue == newValue) {
			return;
		}

		callAndDispatchConversion(category, unit, newValue, false);
	};

	return (
		<>
			{/* Linear */}
			<div className='row align-items-center g-0 mb-4'>
				<div className="col-12 col-md-4 col-conversion-category-label">
					<span>Linear</span>
				</div>
				<div className="col-12 col-md-8">
					<div className="row align-items-center mx-md-0">
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-linear-kmph"
								label={''}
								unitLabel={'Km/h'}
								value={speedConversionState.linear.kmph}
								min={0}
								onChange={(e) => handleInputChange('linear', 'kmph', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'linear', 'kmph', speedConversionState.linear.kmph ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-linear-mph"
								label={''}
								unitLabel={'mph'}
								value={speedConversionState.linear.mph}
								min={0}
								onChange={(e) => handleInputChange('linear', 'mph', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'linear', 'mph', speedConversionState.linear.mph ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
					<div className="row align-items-center mx-md-0">
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-linear-mps"
								label={''}
								unitLabel={'m/s'}
								value={speedConversionState.linear.mps}
								min={0}
								onChange={(e) => handleInputChange('linear', 'mps', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'linear', 'mps', speedConversionState.linear.mps ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-linear-inps"
								label={''}
								unitLabel={'in/s'}
								value={speedConversionState.linear.inps}
								min={0}
								onChange={(e) => handleInputChange('linear', 'inps', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'linear', 'inps', speedConversionState.linear.inps ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-linear-ftps"
								label={''}
								unitLabel={'ft/s'}
								value={speedConversionState.linear.ftps}
								min={0}
								onChange={(e) => handleInputChange('linear', 'ftps', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'linear', 'ftps', speedConversionState.linear.ftps ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Radius */}
			<div className='row align-items-center g-0 mb-4'>
				<div className="col-12 col-md-4 col-conversion-category-label">
					<span>Radius</span>
				</div>
				<div className="col-12 col-md-8">
					<div className="row align-items-center mx-md-0">
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-radius-mm"
								label={''}
								unitLabel={'mm'}
								value={speedConversionState.radius.mm}
								min={0}
								onChange={(e) => handleInputChange('radius', 'mm', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'radius', 'mm', speedConversionState.radius.mm ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-radius-in"
								label={''}
								unitLabel={'in'}
								value={speedConversionState.radius.in}
								min={0}
								onChange={(e) => handleInputChange('radius', 'in', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'radius', 'in', speedConversionState.radius.in ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Rotation */}
			<div className='row align-items-center g-0 mb-4'>
				<div className="col-12 col-md-4 col-conversion-category-label">
					<span>Rotation</span>
				</div>
				<div className="col-12 col-md-8">
					<div className="row align-items-center mx-md-0">
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-rotation-rpm"
								label={''}
								unitLabel={'RPM'}
								value={speedConversionState.rotation.rpm}
								min={0}
								onChange={(e) => handleInputChange('rotation', 'rpm', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'rotation', 'rpm', speedConversionState.rotation.rpm ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-rotation-radps"
								label={''}
								unitLabel={'rad/s'}
								value={speedConversionState.rotation.radps}
								min={0}
								onChange={(e) => handleInputChange('rotation', 'radps', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'rotation', 'radps', speedConversionState.rotation.radps ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default TabSpeed;
